/* eslint-disable ember/classic-decorator-hooks, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/require-computed-property-dependencies, ember/require-tagless-components */
// Extends From
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
// Ember Imports
import { assert } from '@ember/debug';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
// Internal Imports
import NotificationPreference from '@linc-technologies/linced-common/enums/person-option-notification-preference';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import { getDeviceInfo } from '@linc-technologies/linced-common/utils/platform';
import { classNames } from '@ember-decorators/component';

// notificationCopytext provides copytext to contact the school if a user isn't
// receiving notifications.
const contactSchoolCopytext =
  'If you are not receiving notifications, please contact your school to ensure they have the correct details on file.';

// copyType enables choosing between copytext that is for schools, or being more
// personable.
const copyType = {
  // Admin provides text copy that explains things from the school's point of
  // view.
  Admin: 1,
  // User provides text copy that explains things from a user's point of view.
  User: 2,
};

// notificationCopytext provides notification preference explanation copytext
// that differs based on permission level.
const notificationCopytext = {
  [copyType.Admin]: {
    [NotificationPreference.DEFAULT]:
      'By default, Hero will send push notifications to all devices a user has. If a user has no devices, and their email is set, Hero will attempt to send notifications as emails.<br><br>If the user is not receiving notifications, please make sure that the correct email address is stored on file.<br>',
    [NotificationPreference.EMAIL]:
      'The user has enabled receiving notifications by email.<br>Please note, Hero limits the length of the content sent via email notifications, so in certain cases they may need to sign in to view the whole message.<br>',
    [NotificationPreference.NONE]:
      'The user has disabled receiving notifications from Hero.<br>',
  },
  [copyType.User]: {
    [NotificationPreference.DEFAULT]: `By default, Hero will send push notifications to all your devices.<br>If you have no devices, and we have your correct email address on file, you will receive notifications as emails.<br><br>${contactSchoolCopytext}<br>`,
    [NotificationPreference.EMAIL]: `You have chosen to receive notifications by email.<br>Please note, Hero limits the length of the content sent via email notifications, so in certain cases you may need to sign in to view the whole message.<br><br>${contactSchoolCopytext}<br>`,
    [NotificationPreference.NONE]:
      'You have disabled receiving notifications from Hero.<br>',
  },
};

// smsCopytext provides copytext to assure the user/school that they will still
// receive health and safety critical notifications via txt.
const smsCopytext = {
  [copyType.Admin]:
    "Note: The user's configured notification preference <i>will not</i> disable SMS messages from Hero being sent through.<br>",
  [copyType.User]:
    'If your school has SMS (text messaging) enabled, your notification preference <i>will not</i> disable txts from being sent to you. Generally speaking, schools will use text messaging as a matter of health and safety.<br>If you would like to have SMS notifications disabled, you will need to contact your school to do this on your behalf.<br>',
};

/**
 * user-devices provides a component to list user devices in order to manage
 * devices. Also enables configuring user notification preferences.
 */
@classNames('user-devices')
export default class UserDevices extends Component {
  NotificationPreference = NotificationPreference;
  Scopes = Scopes;

  @service
  session;

  @service
  permissions;

  @service
  personOptions;

  // Attributes
  // devices provides an array of user devices to display.
  // @required
  devices = null;

  // person takes in the person who these devices relate to.
  // @required
  person = null;

  // myDevices, if false, sets the "My Devices" header to "{{fName}}'s Devices".
  // @optional
  myDevices = true;

  // State
  // currentDeviceId is configured on component init, as it relies on data from
  // local storage.
  // @optional
  @tracked
  currentDeviceId = '';

  // Methods
  init() {
    super.init(...arguments);

    // Load local device ID from local storage.
    let deviceId = '';
    const currentDevice = getDeviceInfo();
    if (currentDevice) {
      deviceId = currentDevice.physicalId.toLowerCase();
    }
    this.currentDeviceId = deviceId;
  }

  // canEdit specifies whether the current user is allowed to manage the given
  // devices.
  @computed('session.person.id', 'person.id')
  get canEdit() {
    if (this.session.person.id === this.person.id) {
      return true;
    }

    return this.permissions.meet(Scopes.TENANT_ADMIN);
  }

  @computed('person.options.notificationPreference')
  get notificationPreference() {
    return this.personOptions.getNotificationPreference(this.person);
  }

  // preferenceExplanation returns a human understandable explanation of the
  // notification preference that has been set.
  @computed('notificationPreference')
  get preferenceExplanation() {
    if (
      this.permissions.meet(Scopes.TENANT_ADMIN) &&
      this.session.person.id !== this.person.id
    ) {
      return notificationCopytext[copyType.Admin][this.notificationPreference];
    }

    return notificationCopytext[copyType.User][this.notificationPreference];
  }

  // smsExplanation returns an extra explanation mentioning that SMS
  // notifications will still be onsent to the user as a matter of health and
  // safety.
  @computed('session.scopes')
  get smsExplanation() {
    if (
      this.permissions.meet(Scopes.TENANT_ADMIN) &&
      this.session.person.id !== this.person.id
    ) {
      return smsCopytext[copyType.Admin];
    }

    return smsCopytext[copyType.User];
  }

  @action
  setNotificationPreference(preference) {
    this.personOptions.setPersonNotificationPreference(this.person, preference);
  }

  // Lifecycle hooks
  didReceiveAttrs() {
    assert(
      'component: user-devices requires an array of devices',
      this.devices !== null,
    );
    assert(
      'component: user-devices requires a person record',
      this.person !== null,
    );
    super.didReceiveAttrs(...arguments);
  }
}
